
import type { PropType } from 'vue'
import { computed, defineComponent, ref } from 'vue'
import { useDebounceFn } from '../../../composables/use-debounce-fn'
import { UiPopover } from '../../keen-ui'
import { EpbText } from '../../shared/epb-text'
import { EpbTextbox } from '../../shared/epb-textbox'

export type searchFn = (value: string) => Promise<string[]>

export default defineComponent({
  name: 'EpbAutocompleteSearchbox',
  components: { EpbText, EpbTextbox, UiPopover },
  props: {
    value: {
      type: String,
      required: true,
    },
    searchFn: {
      type: Function as PropType<searchFn>,
      required: true,
    },
    minSearchChars: {
      type: Number,
      default: 3,
    },
    debounceTime: {
      type: Number,
      default: 300,
    },
    copyToClipboard: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    info: {
      type: String,
      default: '',
    },
    error: {
      type: String,
      default: '',
    },
    noResults: {
      type: String,
      default: '',
    },
  },
  emits: ['input', 'enter', 'select'],
  setup(props, { emit }) {
    const baseClass = 'epb-autocomplete-searchbox'

    const popoverRef = ref(null)
    const inputRef = ref(null)
    const suggestions = ref<string[]>([])
    const loading = ref(false)

    const hasMinimumSearchLength = computed(() => props.value.length >= props.minSearchChars)

    const hidePopover = computed(
      () => (!hasMinimumSearchLength.value && !suggestions.value.length) || loading.value,
    )

    const debouncedFetchSuggestions = useDebounceFn(fetchSuggestions, props.debounceTime)

    async function fetchSuggestions(value: string): Promise<void> {
      if (hasMinimumSearchLength.value) {
        loading.value = true
        suggestions.value = []

        try {
          suggestions.value = await props.searchFn(value)
        } finally {
          popoverRef.value.open()
          loading.value = false
        }
      }
    }

    function onInputChange(value: string): void {
      emit('input', value)

      debouncedFetchSuggestions(value)
    }

    let hasFocusedOnce = false
    function onInputFocus(): void {
      if (!hasFocusedOnce && !hidePopover.value) {
        debouncedFetchSuggestions(props.value)
      }

      hasFocusedOnce = true
    }

    function onOptionClick(value: string): void {
      closePopover()

      emit('input', value)
      emit('select', value)
    }

    function getInput(): HTMLInputElement | undefined {
      return inputRef.value.$el.getElementsByTagName('input')?.[0]
    }

    function closePopover(): void {
      popoverRef.value?.close({ returnFocus: false })
    }

    function onPopoverReveal(): void {
      if (hidePopover.value) {
        closePopover()
      }

      const inputElment = getInput()
      inputElment?.focus()
    }

    function onPopoverHide(): void {
      popoverRef.value.returnFocus = false
    }

    return {
      baseClass,
      popoverRef,
      inputRef,
      suggestions,
      loading,
      onInputChange,
      onInputFocus,
      hidePopover,
      onPopoverReveal,
      onOptionClick,
      onPopoverHide,
      closePopover,
    }
  },
})
